import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Text,
  LabelList
} from 'recharts';
import { makeStyles } from '@material-ui/core';
import { PortfolioSplitTypeChartData } from 'store/modules/Charts/types';
import { dummyData } from '../Utils';
import { TooltipPortfolioSplit } from '../Tooltips';
import { formatCurrency } from 'utils';
import { useTheme } from '@material-ui/styles';
import { radiusVertical } from 'utils/charts';

const useStyles = makeStyles({
  bigFonts: {
    fontSize: '18px'
  }
});

interface PortfolioSplitProps {
  data: Array<PortfolioSplitTypeChartData>;
  isAnimationActive?: boolean;
  bigLabels?: boolean;
  height?: number;
}

interface TickProps {
  x: number;
  y: number;
  payload: any;
  bigLabels: boolean;
}

const CustomizedAxisTick = ({ x, y, payload, bigLabels }: TickProps) => {
  return (
    <Text
      x={x}
      y={y + 20}
      width={85}
      textAnchor="middle"
      verticalAnchor="start"
      style={{ color: 'red', fontSize: bigLabels ? '18px' : 'inherit' }}
    >
      {payload.value}
    </Text>
  );
};

const PortfolioSplit = ({
  data,
  isAnimationActive = true,
  bigLabels = false,
  height = 380
}: PortfolioSplitProps) => {
  const theme = useTheme() as any;
  const isDataProvided = data && data.length > 0;
  const dataToRender = isDataProvided ? data : dummyData.portfolioSplit;
  const classes = useStyles();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={dataToRender}
        barCategoryGap="5"
        margin={{ top: 20, right: 10, left: bigLabels ? 55 : 40, bottom: 50 }}
      >
        {isDataProvided && (
          <XAxis
            dataKey="type"
            interval={0}
            axisLine={isDataProvided}
            tickLine={false}
            tick={(props: TickProps) => (
              <CustomizedAxisTick
                x={props.x}
                y={props.y}
                payload={props.payload}
                bigLabels={bigLabels}
              />
            )}
          />
        )}

        {isDataProvided && (
          <YAxis
            dataKey="cash_amount"
            axisLine={isDataProvided}
            tickLine={false}
            tick={{ fontSize: bigLabels ? '18px' : 'inherit' }}
            tickFormatter={(val: string) => formatCurrency(val)}
          />
        )}
        {isDataProvided && <Tooltip content={TooltipPortfolioSplit} />}

        <Bar
          dataKey="cash_amount"
          maxBarSize={90}
          isAnimationActive={isAnimationActive}
          radius={radiusVertical}
        >
          <LabelList
            dataKey="risk_score"
            position="top"
            className={bigLabels ? classes.bigFonts : ''}
          />
          {dataToRender.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={theme.palette.baseColors.charts['charting'][index]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default PortfolioSplit;
